import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { Activity, Flame, Sparkles, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Landing() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2C332A] relative overflow-hidden flex flex-col">
      <div
        className="absolute inset-0 -z-10 opacity-50"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=1920&q=70')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#F7F5F0]/85 via-[#F7F5F0]/85 to-[#F7F5F0]" />

      {/* Sticky header — sign-in always visible */}
      <header className="safe-top px-4 pt-3 pb-3 flex items-center justify-between gap-2 border-b border-[#E6E2D8]/50 bg-[#F7F5F0]/95 backdrop-blur-md">
        <div className="flex items-center gap-2 min-w-0">
          <img src="/logo.png" alt="Fitjong" className="w-8 h-8 rounded-lg shrink-0" />
          <span className="font-semibold text-base" style={{ fontFamily: "Outfit, sans-serif" }}>
            Fitjong
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link to="/login">
            <Button
              data-testid="nav-login-btn"
              size="sm"
              variant="ghost"
              className="rounded-full text-[#5F7161] hover:bg-[#5F7161]/10 font-medium px-4"
            >
              {t("nav.sign_in")}
            </Button>
          </Link>
          <Link to="/login?mode=signup">
            <Button
              data-testid="nav-signup-btn"
              size="sm"
              className="rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white px-4"
            >
              {t("nav.sign_up")}
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero — fits in one viewport */}
      <main className="flex-1 px-5 pt-8 pb-32 flex flex-col justify-center max-w-md mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p
            className="text-[11px] uppercase tracking-[0.25em] text-[#8AA48F] mb-3 font-medium"
            style={{ fontFamily: "DM Sans, sans-serif" }}
          >
            {t("landing.tagline")}
          </p>
          <h1
            style={{ fontFamily: "Outfit, sans-serif" }}
            className="text-3xl font-light tracking-tight leading-[1.1] mb-3"
          >
            <span className="text-[#5F7161] font-medium">{t("landing.headline.match")}</span>{" "}
            {t("landing.headline.move")}{" "}
            <span className="text-[#5F7161] font-medium">{t("landing.headline.repeat")}</span>
          </h1>
          <p className="text-sm text-[#2C332A]/70 mb-6">
            {t("landing.subhead")}
          </p>

          <Link to="/login?mode=signup" className="block">
            <Button
              data-testid="hero-cta-start-btn"
              className="w-full rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white h-12 text-base font-medium"
            >
              {t("landing.cta_start")} <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
          <Link to="/login" className="block mt-3">
            <Button
              data-testid="hero-cta-signin-btn"
              variant="outline"
              className="w-full rounded-full border-[#E6E2D8] hover:bg-white h-11 text-sm"
            >
              {t("landing.cta_have_account")}
            </Button>
          </Link>

          <div className="mt-8 grid grid-cols-3 gap-2 text-center">
            {[
              { icon: Activity, key: "feature_streaks" },
              { icon: Flame, key: "feature_calories" },
              { icon: Sparkles, key: "feature_rewards" },
            ].map((f) => (
              <div
                key={f.key}
                className="p-3 rounded-xl bg-white/60 border border-white/40"
              >
                <f.icon className="text-[#5F7161] mx-auto mb-1" strokeWidth={1.8} size={18} />
                <p className="text-[11px] text-[#2C332A]/70 font-medium">{t(`landing.${f.key}`)}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
